import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  VendorAdvance,
  VendorAdvanceVm,
  VendorAdvanceListResponse,
  Account,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Vendor Advance management service
 * Handles all vendor advance-related API operations
 */
export class VendorAdvanceService {
  /**
   * Get paginated list of vendor advances for a branch
   */
  async getAllAdvances(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<VendorAdvanceListResponse> {
    const url = appendSortDir(endpoints.vendorAdvances.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<VendorAdvanceListResponse> | VendorAdvanceListResponse | any>(url)
    const payload = response.data

    // Helper to map advances array
    const mapAdvances = (advances: any[]): VendorAdvance[] => {
      return advances.map(adv => this.mapAdvanceData(adv))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapAdvances(data),
          }
        }
        return data as VendorAdvanceListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapAdvances(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapAdvances(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapAdvances(payload.data) : [],
    }
  }

  /**
   * Get vendor advance by ID
   */
  async getAdvanceById(branchId: number, id: number): Promise<VendorAdvance> {
    const url = endpoints.vendorAdvances.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<VendorAdvance> | VendorAdvance | any>(url)
    
    let advanceData: any
    
    if ('data' in response.data && 'success' in response.data) {
      advanceData = response.data.data
    } else if ('data' in response.data) {
      advanceData = response.data.data
    } else {
      advanceData = response.data
    }
    
    return this.mapAdvanceData(advanceData)
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<Account[]> {
    const url = endpoints.vendorAdvances.getAllAccounts(branchId)
    const response = await apiClient.get<ApiResponse<Account[]> | Account[] | any>(url)
    
    let accountsData: any[] = []
    
    if (Array.isArray(response.data)) {
      accountsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      accountsData = response.data.data
    }
    
    // Map to Account format
    return accountsData.map((item: any) => ({
      id: item.id || item.accountId || 0,
      accountId: item.accountId || item.id || 0,
      accountName: item.accountName || item.accountNameEn || item.name || '',
      accountNameEn: item.accountNameEn || item.accountName || item.nameEn || '',
      accountNameAr: item.accountNameAr || item.nameAr || '',
      accountCode: item.accountCode || item.code || '',
    }))
  }

  /**
   * Get user IP address
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Insert vendor advance
   */
  async insertAdvance(advance: VendorAdvanceVm): Promise<VendorAdvance> {
    const url = endpoints.vendorAdvances.insert
    
    // Get IP address if not provided
    const advanceWithIP = {
      ...advance,
      ipAddress: advance.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('%c=== VendorAdvanceService.insertAdvance ===', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%cPayload (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(advanceWithIP, null, 2))
    console.log('%cPayload (Object):', 'color: #009688; font-weight: bold;', advanceWithIP)
    console.log('%c===========================================', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.post<ApiResponse<VendorAdvance> | VendorAdvance>(url, advanceWithIP)
    
    console.log('%c=== API Response ===', 'background: #607D8B; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cResponse Status:', 'color: #4CAF50; font-weight: bold;', response.status)
    console.log('%cResponse Data (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(response.data, null, 2))
    console.log('%cResponse Data (Object):', 'color: #009688; font-weight: bold;', response.data)
    console.log('%c====================', 'background: #607D8B; color: white; padding: 5px; font-weight: bold;')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapAdvanceData((response.data as ApiResponse<VendorAdvance>).data)
    }
    
    return this.mapAdvanceData(response.data as VendorAdvance)
  }

  /**
   * Update vendor advance
   */
  async updateAdvance(advance: VendorAdvanceVm): Promise<VendorAdvance> {
    const url = endpoints.vendorAdvances.update
    
    // Get IP address if not provided
    const advanceWithIP = {
      ...advance,
      ipAddress: advance.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('%c=== VendorAdvanceService.updateAdvance ===', 'background: #FF9800; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%cPayload (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(advanceWithIP, null, 2))
    console.log('%cPayload (Object):', 'color: #009688; font-weight: bold;', advanceWithIP)
    console.log('%c==========================================', 'background: #FF9800; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.put<ApiResponse<VendorAdvance> | VendorAdvance>(url, advanceWithIP)
    
    console.log('%c=== API Response ===', 'background: #607D8B; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cResponse Status:', 'color: #4CAF50; font-weight: bold;', response.status)
    console.log('%cResponse Data (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(response.data, null, 2))
    console.log('%cResponse Data (Object):', 'color: #009688; font-weight: bold;', response.data)
    console.log('%c====================', 'background: #607D8B; color: white; padding: 5px; font-weight: bold;')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapAdvanceData((response.data as ApiResponse<VendorAdvance>).data)
    }
    
    return this.mapAdvanceData(response.data as VendorAdvance)
  }

  /**
   * Map API response to VendorAdvance interface
   */
  private mapAdvanceData(advanceData: any): VendorAdvance {
    return {
      id: advanceData.id || 0,
      advNo: advanceData.advNo || advanceData.advanceNo || '',
      vendorId: advanceData.vendorId || 0,
      vendorName: advanceData.vendorName || advanceData.vendorNameEn || advanceData.vendorNameAr || '',
      vendorNameEn: advanceData.vendorNameEn || advanceData.vendorName || '',
      vendorNameAr: advanceData.vendorNameAr || '',
      billRef: advanceData.billRef || advanceData.billReference || '',
      paymentRef: advanceData.paymentRef || advanceData.paymentReference || '',
      date: advanceData.date || '',
      amount: advanceData.amount || 0,
      creditAccount: advanceData.creditAccount || advanceData.creditAccountId || 0,
      creditAccountName: advanceData.creditAccountName || '',
      paymentType: advanceData.paymentType || '',
      refNo: advanceData.refNo || advanceData.refNumber || '',
      note: advanceData.note || '',
      branchId: advanceData.branchId || 0,
      ipAddress: advanceData.ipAddress || '',
    } as VendorAdvance
  }
}

export const vendorAdvanceService = new VendorAdvanceService()

